import { useEffect, useState } from "react";
import api from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function Kpi({ label, value }) {
  return (
    <div className="border border-foreground p-4">
      <p className="font-serif text-3xl leading-none">{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

// Horizontal bar list; `metric` picks which numeric field drives the bar + label.
function BarList({ title, rows, metric = "revenue", format = inr, sub }) {
  const max = Math.max(1, ...rows.map((r) => r[metric] || 0));
  return (
    <div className="border border-foreground p-5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.name + i}>
              <div className="flex justify-between items-baseline gap-3 mb-1">
                <span className="font-serif text-[15px] leading-tight truncate">{r.name}{r.brand ? <span className="text-muted-foreground text-xs"> · {r.brand}</span> : ""}</span>
                <span className="font-mono text-xs whitespace-nowrap">{format(r[metric])}{sub ? <span className="text-muted-foreground"> {sub(r)}</span> : ""}</span>
              </div>
              <div className="h-1.5 bg-foreground/10">
                <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${Math.round((r[metric] || 0) / max * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  if (err) return <p className="font-serif text-2xl text-muted-foreground">Couldn't load insights.</p>;
  if (!data) return <p className="font-mono text-sm">Loading insights…</p>;
  const t = data.totals;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <Kpi label="Revenue" value={inr(t.revenue)} />
        <Kpi label="Orders" value={t.orders} />
        <Kpi label="Avg order value" value={inr(t.avg_order_value)} />
        <Kpi label="Units sold" value={t.units} />
        <Kpi label="Buyers" value={t.unique_buyers} />
        <Kpi label="Repeat rate" value={`${t.repeat_rate}%`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <BarList title="Top products by revenue" rows={data.top_products} metric="revenue" sub={(r) => `· ${r.units}u`} />
        <BarList title="Top categories" rows={data.top_categories} metric="revenue" sub={(r) => `· ${r.units}u`} />
        <BarList title="Top brands" rows={data.top_brands} metric="revenue" sub={(r) => `· ${r.units}u`} />
        <BarList title="Size distribution (units)" rows={data.size_distribution} metric="units" format={(n) => `${n}u`} />
        <BarList title="Department mix (units)" rows={data.department_mix} metric="units" format={(n) => `${n}u`} />
        <BarList title="Top states (orders)" rows={data.top_states} metric="orders" format={(n) => `${n}`} />
        <BarList title="Revenue by month" rows={data.revenue_by_month} metric="revenue" />
      </div>
      <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Based on all paid orders. Export the raw data from Orders → Product lines (CSV).</p>
    </div>
  );
}
